import React from 'react';
import type {
  RankingResultT,
  VisualizationTranslationFn,
} from '@mentimeter/visualizations';
import { Ranking } from '@mentimeter/visualizations';
import { usePresentationContext } from '@mentimeter/question-modules-contexts';
import {
  SvgElement,
  VisualizationUpdater,
  useUniqueSelector,
} from '@mentimeter/question-modules-shared';
import type { RankingT } from '@mentimeter/question-modules-types';
import { ScreenReaderOnly } from '@mentimeter/ragnar-ui/screenreader-only';
import { useRagnar } from '@mentimeter/ragnar-react';
import { useTranslations } from 'next-intl';
import { ScreenReaderTable } from './ScreenReaderTable';

interface DataT extends RankingResultT {
  index: number;
}

interface Props {
  selector: string;
  data: DataT[];
  fillColors: string[];
  textColor: string;
  animationMultiplier: 0 | 1;
  translate: VisualizationTranslationFn;
  isScoreHidden: boolean;
}

export const WIDTH = 1460;
export const HEIGHT = 540;

const C = React.memo<Props>(
  ({
    selector,
    data,
    fillColors,
    textColor,
    animationMultiplier,
    translate,
    isScoreHidden,
  }) => {
    const ranking = new Ranking({
      selector: `#${selector}`,
      data,
      fillColors,
      textColor,
      animationMultiplier,
      translate,
      width: WIDTH,
      height: HEIGHT,
      isScoreHidden,
    });

    return (
      <SvgElement aria-hidden viewBox={`0 0 ${WIDTH} ${HEIGHT}`}>
        <VisualizationUpdater updateFn={ranking.update} />
        <g id={selector} />
      </SvgElement>
    );
  },
);

export const RankingVisualization = () => {
  const { useConfig, useResults, useQuestion } = usePresentationContext();
  const {
    theme: {
      visualizationColors: { fillColors, textColor },
    },
  } = useRagnar();
  const { enableAnimations } = useConfig();
  const { results = [] } = useResults<RankingT>();
  const translate = useTranslations('presenting.legacy');
  const selector = useUniqueSelector();
  const { choices, hide_results } = useQuestion();

  const data = React.useMemo(() => {
    return choices.map((choice, i) => {
      const result = results.find((result) => result.id === choice.id);
      const fallbackResult = {
        id: choice.id,
        label: choice.label,
        score: 0,
      };
      return { ...(result ?? fallbackResult), index: i };
    });
  }, [choices, results]);

  return (
    <>
      <C
        selector={selector}
        data={data}
        fillColors={fillColors}
        textColor={textColor}
        animationMultiplier={enableAnimations ? 1 : 0}
        translate={translate}
        isScoreHidden={hide_results}
      />
      <ScreenReaderOnly>
        <ScreenReaderTable results={results} shouldHideResults={hide_results} />
      </ScreenReaderOnly>
    </>
  );
};
